import React from 'react';
import { positionLabel, positionDirectionTone } from './lib/position';
import { DIRECTION } from './lib/colors';

/**
 * The direction line under the Position tile (spec v2 §4, §8).
 *
 * Renders `facts.position_direction` VERBATIM, with an arrow and a colour keyed to that
 * string and to nothing else — never to `avg_position`, never to `position_change_pct`.
 * §8: "green up for higher, orange down for lower, nothing otherwise."
 *
 * The glyph is `aria-hidden` because the sentence beside it already says the same thing
 * in words; a screen reader announcing "up arrow, higher on the results page than a year
 * ago" is the arrow read twice. Colour is never the only signal — the words carry the
 * meaning on their own, which is what keeps the tile readable in greyscale and in print.
 *
 * Renders nothing when the direction is absent: §8 says show the number and no
 * direction, which means no placeholder, no dash and no "unchanged".
 */
const PositionDirection = ({ direction }) => {
  const label = positionLabel(direction);
  if (!label) return null;

  const tone = positionDirectionTone(direction);
  const glyph = tone === 'improved' ? '▲' : tone === 'declined' ? '▼' : '';
  const color = tone === 'neutral' ? undefined : DIRECTION[tone];

  return (
    <span
      data-testid="position-direction"
      data-tone={tone}
      // Not a flex row: the direction string is a full sentence and wraps to two or
      // three lines in a tile, and a flex glyph would sit on a line of its own above it.
      // Inline keeps the arrow hugging the first word, the way a bullet does.
      className="block"
      style={color ? { color } : undefined}
    >
      {glyph && <span aria-hidden="true" className="mr-1">{glyph}</span>}
      <span>{label}</span>
    </span>
  );
};

export default PositionDirection;
